import React from 'react';
import { Modal } from '../ui/Modal';

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'privacy' | 'terms' | 'disclaimer';
}

export function LegalModal({ isOpen, onClose, type }: LegalModalProps) {
  const titles = {
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    disclaimer: 'Medical Disclaimer',
  };

  const contents = {
    privacy: (
      <div className="space-y-3 text-sm text-on-surface-variant">
        <p>Last updated: April 2026</p>
        <p>
          At 222fasting, we respect your privacy and are committed to protecting your personal
          data. This privacy policy explains how we collect, use, and safeguard your information
          when you use our platform.
        </p>
        <h4 className="font-semibold text-on-surface">Information We Collect</h4>
        <p>
          We collect information you provide directly to us, including your email address, weight
          data, blood test results, and profile information.
        </p>
        <h4 className="font-semibold text-on-surface">How We Use Your Data</h4>
        <p>
          We use your data to provide and improve our services, including tracking your fasting
          progress, analyzing blood results, and delivering coaching content.
        </p>
        <h4 className="font-semibold text-on-surface">Data Retention</h4>
        <p>
          We retain your personal data while your account is active. You can delete your account
          at any time, and we will anonymize your data after 30 days.
        </p>
      </div>
    ),
    terms: (
      <div className="space-y-3 text-sm text-on-surface-variant">
        <p>Last updated: April 2026</p>
        <p>
          By accessing or using 222fasting, you agree to be bound by these Terms of Service and
          all applicable laws and regulations.
        </p>
        <h4 className="font-semibold text-on-surface">Use License</h4>
        <p>
          Permission is granted to temporarily view the materials (information or software) on
          222fasting's website for personal, non-commercial transitory viewing only.
        </p>
        <h4 className="font-semibold text-on-surface">Subscription Services</h4>
        <p>
          By subscribing to 222fasting Pro, you agree to pay the subscription fees. Subscriptions
          are billed monthly or annually at your selection.
        </p>
        <h4 className="font-semibold text-on-surface">Disclaimer</h4>
        <p>
          The materials on 222fasting's website are provided on an "as is" basis without
          warranties of any kind.
        </p>
      </div>
    ),
    disclaimer: (
      <div className="space-y-3 text-sm text-on-surface-variant">
        <p className="font-semibold text-error">IMPORTANT MEDICAL DISCLAIMER</p>
        <p>
          The content provided on 222fasting is for informational and educational purposes only
          and is not intended as medical advice, diagnosis, or treatment.
        </p>
        <p>
          Extended fasting is a serious practice that carries potential risks including but not
          limited to electrolyte imbalances, nutrient deficiencies, and other health complications.
        </p>
        <p>
          <strong>YOU MUST CONSULT WITH A QUALIFIED HEALTHCARE PROFESSIONAL</strong> before
          beginning any fasting protocol, especially if you have any pre-existing medical
          conditions such as:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Diabetes (type 1 or type 2)</li>
          <li>Heart disease</li>
          <li>Kidney disease</li>
          <li>Eating disorders</li>
          <li>Pregnancy or breastfeeding</li>
          <li>Any chronic medical condition</li>
        </ul>
        <p>
          Fasting insulin levels below 6 mIU/L, potassium below 3.0 mmol/L, or albumin below 30
          g/L require immediate medical attention.
        </p>
        <p>
          222fasting, its creators, and contributors are not liable for any adverse outcomes
          resulting from the use of this platform.
        </p>
      </div>
    ),
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={titles[type]} size="lg">
      <div className="max-h-[60vh] overflow-y-auto pr-2">{contents[type]}</div>
    </Modal>
  );
}
